import { Box, CircularProgress } from '@material-ui/core';
import { Typography } from 'antd';
import _ from 'lodash';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generate_token } from 'src/app/utils/commomService';
import { closeLoading, showLoading } from 'src/features/videoSlice';
import styled from 'styled-components';
import VGPSPlayer from './VGPSPlayer';
import './style.css';

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  background-color: #4c4c4c;
  background-clip: content-box;
  border: 0.5px solid #ffffff;
  object-fit: fill;
`;

const CDN_URL = process.env.REACT_APP_CDN_WSS_SERVER;

const Video = React.memo(({ id, height, controls, onSignalStatusChange, playerRef, signalStatus }) => {
  const videoRef = useRef();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (videoRef.current && id) {
      if (!playerRef.current[id]) {
        const token = generate_token(32);
        const ws_url = `${CDN_URL}/evup/${token}/${id}`;
        playerRef.current[id] = new VGPSPlayer(videoRef.current, { transport: ws_url });
        playerRef.current[id].token = token;
      } else if (playerRef.current[id].videoElement !== videoRef.current) {
        playerRef.current[id].updateVideoElement(videoRef.current);
      }
    }

    return () => {
      if (id && playerRef.current[id]) {
        playerRef.current[id].stop();
        delete playerRef.current[id];
      }
    };
  }, [id, playerRef]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      let timeoutId;
      const handleError = () => {
        setIsLoading(false);
        onSignalStatusChange(id, false);
      };
      const handleCanPlay = () => {
        setIsLoading(false);
        onSignalStatusChange(id, true);
        clearTimeout(timeoutId);
      };
      const handleStalled = () => {
        setIsLoading(false);
        onSignalStatusChange(id, false);
      };

      timeoutId = setTimeout(() => {
        setIsLoading(false);
        onSignalStatusChange(id, false);
      }, 5000);

      videoEl.addEventListener('error', handleError);
      videoEl.addEventListener('canplay', handleCanPlay);
      videoEl.addEventListener('stalled', handleStalled);

      return () => {
        videoEl.removeEventListener('error', handleError);
        videoEl.removeEventListener('canplay', handleCanPlay);
        videoEl.removeEventListener('stalled', handleStalled);
        clearTimeout(timeoutId);
      };
    }
  }, [id, onSignalStatusChange]);

  return (
    <Box style={{ height: height, width: '-webkit-fill-available', position: 'relative' }}>
      <StyledVideo playsInline autoPlay ref={videoRef} id={id} />
      {isLoading && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <CircularProgress color="inherit" />
          <Typography color="inherit">Vui lòng đợi trong giây lát</Typography>
        </Box>
      )}
      {!isLoading && !signalStatus[id] && (
        <Typography
          style={{
            color: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          NO SIGNAL
        </Typography>
      )}
    </Box>
  );
});

const VideoWall = React.memo(({ deviceSerial }) => {
  const dispatch = useDispatch();
  const playerRef = useRef({});
  const [videoMode, setVideoMode] = useState(false);
  const [signalStatus, setSignalStatus] = useState({});
  const [listCameras, setListCameras] = useState([]);
  const dragCameraRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!deviceSerial) {
      setListCameras([]);
      setIsReady(false);
      Object.values(playerRef.current).forEach((player) => player.stop());
      playerRef.current = {};
      return;
    }

    const camerasHD = [
      { id: `${deviceSerial}_05`, name: 'CH5', isMain: false },
      { id: `${deviceSerial}_07`, name: 'CH7', isMain: false },
      { id: `${deviceSerial}_01`, name: 'CH1', isMain: false },
      { id: `${deviceSerial}_03`, name: 'CH3', isMain: true },
    ];
    const camerasSD = [
      { id: `${deviceSerial}_08`, name: 'CH8', isMain: false },
      { id: `${deviceSerial}_06`, name: 'CH6', isMain: false },
      { id: `${deviceSerial}_02`, name: 'CH2', isMain: false },
      { id: `${deviceSerial}_04`, name: 'CH4', isMain: true },
    ];

    setListCameras(videoMode ? camerasHD : camerasSD);
    setIsReady(true);
    dispatch(showLoading({ device_serial: deviceSerial, type: 'keepSerial' }));

    return () => {
      Object.values(playerRef.current).forEach((player) => player.stop());
      playerRef.current = {};
      setIsReady(false);
      dispatch(closeLoading());
    };
  }, [deviceSerial, videoMode, dispatch]);

  const handleSignalStatusChange = useCallback((cameraId, hasSignal) => {
    setSignalStatus((prev) => ({ ...prev, [cameraId]: hasSignal }));
  }, []);

  const handleChangeVideoSolution = useCallback(() => {
    setVideoMode((prev) => !prev);
    if (deviceSerial) {
      dispatch(showLoading({ device_serial: deviceSerial, type: 'keepSerial' }));
    }
  }, [dispatch, deviceSerial]);

  const handleDragStart = useCallback((cameraId) => {
    if (!cameraId || !isReady) {
      return;
    }
    dragCameraRef.current = cameraId;
  }, [isReady]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDragEnd = useCallback(() => {
    dragCameraRef.current = null;
  }, []);

  const handleDrop = useCallback(
    (targetCameraId) => {
      if (!isReady || !targetCameraId || !dragCameraRef.current || dragCameraRef.current === targetCameraId) {
        dragCameraRef.current = null;
        return;
      }

      dispatch(showLoading({ device_serial: deviceSerial, type: 'keepSerial' }));
      
      const clone = _.cloneDeep(listCameras);
      const dragIndex = clone.findIndex((x) => x.id === dragCameraRef.current);
      const dropIndex = clone.findIndex((x) => x.id === targetCameraId);

      if (dragIndex === -1 || dropIndex === -1) {
        dragCameraRef.current = null;
        dispatch(closeLoading());
        return;
      }

      [clone[dragIndex], clone[dropIndex]] = [clone[dropIndex], clone[dragIndex]];
      const mainCamera = listCameras.find((c) => c.isMain);
      const isMainCameraInvolved = dragCameraRef.current === mainCamera?.id || targetCameraId === mainCamera?.id;
      if (isMainCameraInvolved) {
        clone.forEach((cam) => {
          cam.isMain = cam.id === (targetCameraId === mainCamera?.id ? dragCameraRef.current : targetCameraId);
        });
      }
      setListCameras(clone);
      dragCameraRef.current = null;

      setTimeout(() => {
        dispatch(closeLoading());
      }, 5000);
    },
    [listCameras, isReady, dispatch, deviceSerial]
  );

  const cameraMain = listCameras.find((c) => c.isMain);
  const otherCameras = listCameras.filter((c) => !c.isMain);
  const videoHeight = 'calc(100vh - 66px - 44px - 22vh)';

  return (
    <Box style={{ height: 'calc(100vh - 66px)' }}>
      {cameraMain && (
        <Box
          className="row"
          style={{
            position: 'relative',
            left: 0,
            top: 0,
            paddingBottom: '44px',
          }}
        >
          <Box className="col-12 px-0">
            <Box
              style={{
                position: 'relative',
                top: 0,
                left: 0,
                cursor: isReady ? 'move' : 'default',
              }}
              draggable={isReady}
              onDragStart={() => handleDragStart(cameraMain.id)}
              onDragOver={handleDragOver}
              onDrop={() => {
                handleDrop(cameraMain.id);
              }}
              onDragEnd={handleDragEnd}
            >
              <Video
                key={cameraMain.id}
                id={cameraMain.id}
                height={videoHeight}
                controls={cameraMain.isMain}
                onSignalStatusChange={handleSignalStatusChange}
                playerRef={playerRef}
                signalStatus={signalStatus}
              />
            </Box>
          </Box>

          {otherCameras.map((camera) => (
            <Box key={camera.id} className="col-4 px-0">
              <Box
                style={{
                  position: 'relative',
                  cursor: isReady ? 'move' : 'default',
                }}
                draggable={isReady}
                onDragStart={() => handleDragStart(camera.id)}
                onDragOver={handleDragOver}
                onDrop={() => {
                  handleDrop(camera.id);
                }}
                onDragEnd={handleDragEnd}
              >
                <Video
                  id={camera.id}
                  height="22vh"
                  controls={false}
                  onSignalStatusChange={handleSignalStatusChange}
                  playerRef={playerRef}
                  signalStatus={signalStatus}
                />
              </Box>
            </Box>
          ))}

          <Box
            style={{
              display: 'flex',
              backgroundColor: '#D9042F',
              height: '44px',
              padding: '0 25px',
              position: 'absolute',
              boxSizing: 'border-box',
              left: 0,
              bottom: 0,
              width: '100%',
            }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box style={{ display: 'flex', gap: '20px' }}>
                <Typography
                  style={{
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'inline-block',
                  }}
                  onClick={handleChangeVideoSolution}
                >
                  {videoMode ? 'HD' : 'SD'}
                </Typography>
              </Box>
              <Box style={{ display: 'flex', gap: '15px', cursor: 'pointer' }}>
                <Box>
                  <img src="/static/iconSvg/grid/grid4_icon.svg" alt="Grid layout" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
});
export default VideoWall;
